import _React, { useContext, useCallback, useEffect } from 'react';
import { MapContext } from '../contexts/MapContext';
import GeometryService from '../services/GeometryService.js';

/**
 * Hook para gerenciar o processamento de propriedade.
 * Injeta handlePropertyResult no contexto para uso por UnifiedSearchBar e cliques.
 */
export default function usePropertyHandler() {
  const {
    setPropertyData,
    setGeometries,
    setThemeGeometries,
    setActiveLayers,
    setMarker,
    setPropertyCodes,
    setHandlePropertyResult,
    setSidebarOpen,
  } = useContext(MapContext);

  // Função para limpar completamente o contexto
  const clearContext = useCallback(() => {
    // Limpar na ordem correta para evitar renderizações parciais
    setThemeGeometries({}); // Primeiro limpar as geometrias por tema
    setGeometries([]); // Depois limpar o array de geometrias
    setPropertyData(null); // Limpar a propriedade
    setPropertyCodes({ cod_car: null, codigo_imo: null, filename: null }); // Limpar códigos
    setActiveLayers({}); // Limpar camadas ativas
    setMarker(null); // Limpar marcador
    setSidebarOpen(false); // Fechar sidebar
  }, [
    setPropertyData,
    setGeometries,
    setThemeGeometries,
    setActiveLayers,
    setMarker,
    setPropertyCodes,
    setSidebarOpen,
  ]);

  /**
   * Função para processar resultados de propriedade e geometrias
   * @param {Object} properties - Dados da propriedade (CAR, SIGEF, etc.)
   * @param {Object} geometryData - Dados de geometria processados pelo GeometryService
   * @param {Object} extraData - Dados adicionais (opcional)
   */
  const handlePropertyResult = useCallback(
    (properties, geometryData = null, extraData = {}) => {
      // eslint-disable-next-line no-console
      console.log('[usePropertyHandler] Processando nova propriedade:', {
        temGeometryData: Boolean(geometryData),
        extraData,
        propertiesKeys: Object.keys(properties || {}),
      });

      // Verificar se recebemos um objeto em formato antigo (markerPosition) ou novo (geometryData)
      // No formato antigo, o segundo parâmetro é uma posição de marcador e não um objeto de geometrias
      const isLegacyFormat =
        !geometryData ||
        (geometryData &&
          (Array.isArray(geometryData) ||
            typeof geometryData !== 'object' ||
            !('themeGeometries' in geometryData)));

      const markerPosition = isLegacyFormat ? geometryData : null;
      const processedGeometries = isLegacyFormat ? null : geometryData;

      // Primeiro limpar completamente o contexto anterior
      // Usamos setTimeout para garantir que a limpeza ocorra em um ciclo separado de renderização
      setTimeout(() => {
        clearContext();

        // Garantir que properties seja um objeto válido
        if (!properties || typeof properties !== 'object') {
          console.warn('[usePropertyHandler] Dados de propriedade inválidos:', properties);
          return;
        }

        // Processar geometrias com segurança
        try {
          // Usar geometrias pré-processadas ou processar agora
          const { carGeometries, sigefGeometries, themeGeometries } =
            processedGeometries || GeometryService.processPropertyGeometries(properties);

          // eslint-disable-next-line no-console
          console.log('[usePropertyHandler] Geometrias processadas:', {
            carCount: carGeometries?.length || 0,
            sigefCount: sigefGeometries?.length || 0,
            temas: Object.keys(themeGeometries),
            contagem: Object.values(themeGeometries).flat().length,
          });

          // Atualizar estado com novas geometrias - ordem importante!
          // Primeiro definir as geometrias por tema
          setThemeGeometries({ ...themeGeometries });

          // Depois definir o array de geometrias
          setTimeout(() => {
            setGeometries(Object.values(themeGeometries).flat());

            // Definir a propriedade
            setPropertyData({ ...properties });

            // Extrair códigos da propriedade considerando múltiplos objetos CAR e SIGEF
            let cod_imovel = extraData.cod_imovel;
            let codigo_imo = extraData.codigo_imo;
            const filename = extraData.filename;

            // Se não foram fornecidos explicitamente, tenta extrair dos dados da propriedade
            if (!cod_imovel) {
              if (properties.CAR) {
                if (Array.isArray(properties.CAR)) {
                  cod_imovel = properties.CAR.find((item) => item?.cod_imovel)?.cod_imovel;
                } else {
                  cod_imovel = properties.CAR?.cod_imovel;
                }
              } else if (properties.car) {
                if (Array.isArray(properties.car)) {
                  cod_imovel = properties.car.find((item) => item?.cod_imovel)?.cod_imovel;
                } else {
                  cod_imovel = properties.car?.cod_imovel;
                }
              }
            }

            if (!codigo_imo) {
              if (properties.SIGEF) {
                if (Array.isArray(properties.SIGEF)) {
                  codigo_imo = properties.SIGEF.find((item) => item?.codigo_imo)?.codigo_imo;
                } else {
                  codigo_imo = properties.SIGEF?.codigo_imo;
                }
              } else if (properties.sigef) {
                if (Array.isArray(properties.sigef)) {
                  codigo_imo = properties.sigef.find((item) => item?.codigo_imo)?.codigo_imo;
                } else {
                  codigo_imo = properties.sigef?.codigo_imo;
                }
              }
            }

            // NÃO gerar localmente o filename - usar exclusivamente o valor do backend
            // O filename já deve ter sido definido no PropertyService a partir do file_id
            if (!filename) {
              console.warn(
                '[usePropertyHandler] filename não definido na resposta do backend. Isso pode indicar problema na integração backend/frontend.',
              );
            } else {
              // no-op
            }

            // Atualizar códigos da propriedade
            setPropertyCodes({ cod_imovel, codigo_imo, filename });

            // Definir marcador se houver uma posição válida
            if (markerPosition) {
              // Verifica se markerPosition é um objeto com lat/lng ou um array [lng, lat]
              if (Array.isArray(markerPosition)) {
                setMarker({
                  lat: markerPosition[1],
                  lng: markerPosition[0],
                  found: Boolean(cod_imovel || codigo_imo),
                });
              } else if (markerPosition.lat && markerPosition.lng) {
                setMarker({
                  ...markerPosition,
                  found: Boolean(cod_imovel || codigo_imo),
                });
              }
            }

            // Ativar todas as camadas por padrão
            const defaultActive = {};
            Object.keys(themeGeometries).forEach((alias) => {
              defaultActive[alias] = true;
            });
            setActiveLayers(defaultActive);

            // Removida a abertura automática do sidebar conforme solicitação
            // O sidebar só deve abrir quando o usuário clicar no marcador
            // if (Object.keys(properties).length > 0 && (properties.CAR || properties.SIGEF || properties.car || properties.sigef)) {
            //   setSidebarOpen(true);
            // }
          }, 0);
        } catch (error) {
          console.error('[usePropertyHandler] Erro ao processar geometrias:', error);
        }
      }, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      clearContext,
      setGeometries,
      setThemeGeometries,
      setPropertyData,
      setPropertyCodes,
      setMarker,
      setActiveLayers,
      setSidebarOpen,
    ],
  );

  useEffect(() => {
    setHandlePropertyResult(() => handlePropertyResult);
    return () => setHandlePropertyResult(() => () => {});
  }, [handlePropertyResult, setHandlePropertyResult]);
}
